import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { FormValues, validation, STEP_ID } from './DonationYear.meta';
import { INPUTS_WIDTH } from '../../CharitableGiving.meta';
import { useNavigation } from '../../hooks/useNavigation';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Donor, GivingStrategy, WorkflowDTO } from '../../api/useCharitableGivingQuery';
import { Checkbox } from '../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { selectCurrentClient } from '../../../../../CommonSelectors/CommonAreaSelector';

export const DonationYear = () => {
  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const {
    data: { donationStartYear, donationEndYear, hasPartner, givingStrategy, donor },
    options: { partnerQcdYearOptions, principalQcdYearOptions, donationYearOptions },
  } = workflow;

  const [isRecurring, setIsRecurring] = useState(
    donationEndYear !== null && donationStartYear !== donationEndYear,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      donationStartYear,
      donationEndYear,
    },
    resolver: yupResolver(validation),
    context: {
      isRecurring,
    },
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        donationStartYear: values.donationStartYear,
        donationEndYear: isRecurring ? values.donationEndYear : null,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const hasDonor = donor !== null;

  const rawQcdYearOptions =
    hasDonor && donor === Donor.Spouse ? partnerQcdYearOptions : principalQcdYearOptions;

  const rawYearOptions =
    givingStrategy === GivingStrategy.QualifiedCharitableDonations
      ? rawQcdYearOptions
      : donationYearOptions;

  const yearOptions = rawYearOptions.map(year => ({
    label: year.toString(),
    value: year,
    rightLabel:
      primaryYob && hasDonor
        ? donor === Donor.Principal
          ? `Age ${year - primaryYob}`
          : spouseYob
          ? `Age ${year - spouseYob}`
          : ''
        : primaryYob
        ? `Age ${year - primaryYob}${spouseYob ? `, Age ${year - spouseYob}` : ''}`
        : undefined,
  }));

  return (
    <StepContainer
      title="When would you like to make the donation?"
      onSubmit={onSubmit}
      footer={
        <StepFooter
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <Box display="flex" flexDirection="column" style={{ gap: 32 }}>
        <Box display="flex" style={{ gap: 16 }}>
          <Controller
            control={control}
            name="donationStartYear"
            render={({ field: { value, onChange, ref } }) => (
              <Box style={{ width: INPUTS_WIDTH }}>
                <Select
                  innerRef={ref}
                  label={isRecurring ? 'Start Year' : 'Donation Year'}
                  value={yearOptions.find(opt => opt.value === value)}
                  options={yearOptions}
                  onChange={opt => onChange(opt?.value)}
                  formVariant={errors.donationStartYear ? 'error' : undefined}
                  helperText={errors.donationStartYear?.message}
                />
              </Box>
            )}
          />

          {isRecurring && (
            <Controller
              control={control}
              name="donationEndYear"
              render={({ field: { value, onChange, ref } }) => (
                <Box style={{ width: INPUTS_WIDTH }}>
                  <Select
                    innerRef={ref}
                    label="End Year"
                    value={yearOptions.find(opt => opt.value === value)}
                    options={yearOptions}
                    onChange={opt => onChange(opt?.value)}
                    formVariant={errors.donationEndYear ? 'error' : undefined}
                    helperText={errors.donationEndYear?.message}
                  />
                </Box>
              )}
            />
          )}
        </Box>

        <Checkbox checked={isRecurring} onChange={evt => setIsRecurring(evt.target.checked)}>
          Recurring
        </Checkbox>
      </Box>
    </StepContainer>
  );
};
